/**
 * VaultClient factory. In dev (no `VITE_LOCKER_API`) it drives the in-memory
 * `MockTransport` so the whole portal runs with no backend — the Phase-1 server
 * (CT 207) isn't built yet. When `VITE_LOCKER_API` is set it talks to the live
 * API and reads the heivol-id JWT from storage (Phase-1 wires real OIDC).
 *
 * Silent token refresh: heivol-id bearers are short-lived, so `FetchTransport`
 * is wrapped in `RefreshingTransport` — on a 401 from the API's auth middleware
 * it redeems the stored refresh token via POST /auth/refresh (single-flight,
 * rotation-safe) and retries the request exactly once with the new bearer.
 * Only when the refresh itself is rejected do we clear tokens and fall back to
 * the login gate. umk-proof 401s (`umk_proof_invalid`) are NOT retried — they
 * are a vault-lock concern, not an auth concern.
 */
import {
  FetchTransport,
  MockTransport,
  VaultClient,
  type Transport,
  type TransportRequest,
  type TransportResponse,
} from '@heivol-locker/vault-client';

const DEV_SUB = 'dev-user';
const JWT_KEY = 'hv-jwt';
const REFRESH_KEY = 'hv-refresh';

export interface ClientHandle {
  readonly client: VaultClient;
  /** The account identifier the mock keys on (dev only). */
  readonly sub: string;
  readonly usingMock: boolean;
}

function errorCode(json: unknown): string | null {
  if (typeof json === 'object' && json !== null) {
    const err = (json as { error?: { code?: unknown } }).error;
    if (err !== undefined && err !== null && typeof err.code === 'string') {
      return err.code;
    }
  }
  return null;
}

// ---- single-flight silent refresh ------------------------------------------

/** One in-flight refresh shared across concurrent 401s. The IdP ROTATES
 *  refresh tokens, so two parallel refresh calls would invalidate each other
 *  (the second presents an already-rotated token → invalid_grant → forced
 *  re-login). All concurrent callers await the same promise instead. */
let refreshInFlight: Promise<string | null> | null = null;

function refreshOnce(apiBase: string): Promise<string | null> {
  if (refreshInFlight === null) {
    refreshInFlight = doRefresh(apiBase).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Returns the new bearer on success, null when the refresh token is missing
 *  or rejected (→ full re-login required), and THROWS on transient failures
 *  (network / IdP 5xx) so callers surface the original error without
 *  destroying a possibly-still-valid refresh token. */
async function doRefresh(apiBase: string): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (refreshToken === null || refreshToken.length === 0) {
    return null;
  }
  const res = await fetch(`${apiBase}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (res.status === 400 || res.status === 401) {
    return null; // refresh token is dead — re-login
  }
  if (!res.ok) {
    throw new Error(`token refresh failed: ${res.status}`); // transient
  }
  const json = (await res.json()) as { access_token?: unknown; refresh_token?: unknown };
  if (typeof json.access_token !== 'string' || json.access_token.length === 0) {
    return null;
  }
  localStorage.setItem(JWT_KEY, json.access_token);
  if (typeof json.refresh_token === 'string' && json.refresh_token.length > 0) {
    localStorage.setItem(REFRESH_KEY, json.refresh_token); // rotated
  }
  return json.access_token;
}

/** Auth is genuinely gone (refresh token rejected). Clear tokens and reload to
 *  the app root — session.init() sees no JWT and shows the login gate, exactly
 *  the pre-existing auth-loss behaviour. */
function hardLogout(): void {
  try {
    localStorage.removeItem(JWT_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* storage may be unavailable */
  }
  window.location.replace('/');
}

// ---- transport wrapper -------------------------------------------------------

/** Wraps the real transport with on-401 refresh + single retry. Sits below
 *  `VaultClient`, so EVERY API call gets the behaviour. Retrying with the same
 *  umk-proof headers is safe: the middleware 401 fires before umk-proof
 *  verification, so the nonce was never consumed. */
class RefreshingTransport implements Transport {
  private readonly inner: Transport;
  private readonly apiBase: string;

  constructor(inner: Transport, apiBase: string) {
    this.inner = inner;
    this.apiBase = apiBase.replace(/\/$/u, '');
  }

  async request(req: TransportRequest): Promise<TransportResponse> {
    const res = await this.inner.request(req);
    if (res.status !== 401 || errorCode(res.json) !== 'unauthorized') {
      return res; // not an auth-middleware 401 (e.g. umk_proof_invalid) — pass through
    }
    let token: string | null;
    try {
      token = await refreshOnce(this.apiBase);
    } catch {
      return res; // transient refresh failure — surface the original 401, keep tokens
    }
    if (token === null) {
      hardLogout();
      return res;
    }
    return this.inner.request({
      ...req,
      headers: { ...req.headers, authorization: `Bearer ${token}` },
    });
  }
}

export function createClient(): ClientHandle {
  const apiBase = import.meta.env['VITE_LOCKER_API'] as string | undefined;

  if (apiBase !== undefined && apiBase.length > 0) {
    const transport: Transport = new RefreshingTransport(new FetchTransport(apiBase), apiBase);
    const client = new VaultClient({
      transport,
      getJwt: () => localStorage.getItem(JWT_KEY) ?? '',
    });
    return { client, sub: '', usingMock: false };
  }

  const transport = new MockTransport({
    identities: [{ sub: DEV_SUB, email: 'you@heivol.local' }],
  });
  const client = new VaultClient({ transport, getJwt: () => DEV_SUB });
  return { client, sub: DEV_SUB, usingMock: true };
}
